import React, { useState, useRef, useEffect, useCallback } from 'react';
import { skippedMessage } from './services/publishResult';
import { AlertTriangle } from 'lucide-react';
import { ToastHost, toast } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import UploadResultsModal from './components/UploadResultsModal';

// Component Imports
import DashboardTab from './components/tabs/DashboardTab';
import FinalRosterTab from './components/tabs/FinalRosterTab';
import LeaveRosterTab from './components/tabs/LeaveRosterTab';
import StaffTab from './components/tabs/StaffTab';
import OperationsTab from './components/tabs/OperationsTab';
import SettingsTab from './components/tabs/SettingsTab';
import SkillsTab from './components/tabs/SkillsTab';
import Sidebar from './components/layout/Sidebar';
import ShiftTypesPage from './components/tabs/ShiftTypesPage';
import RankMapPage from './components/tabs/RankMapPage';

// Hook Imports
import { useRoster } from './hooks/useRoster';
import { useSelection } from './hooks/useSelection';

// Service Imports
import { getEmployeeStats, getLocationCoverage, isEmployeeUnavailable, getAvailableEmployeesForDay, getAllSkills } from './services/scheduleService';
import { apiFetch } from './services/apiClient';
import { addDraftDates, removeDraftDates, datesBetween, toISODate, fromISODate } from './services/draftDates';
import { scopeShifts, shiftsBetween } from './services/rosterScope';
import { mergeOnCall } from './services/onCallRoster';
import { withTeam } from './services/teams';

// The Excel sheet covers `days` days from the first visible day; send those
// plus a week either side (the server snaps the window to a Monday).
const exportShifts = (shifts, targetDate, days) => {
  // The Final Roster passes a Date; accept an ISO string too.
  const first = targetDate instanceof Date
    ? new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
    : fromISODate(String(targetDate).slice(0, 10));
  const from = new Date(first); from.setDate(from.getDate() - 7);
  const to = new Date(first); to.setDate(to.getDate() + days + 7);
  return shiftsBetween(shifts, toISODate(from), toISODate(to));
};

// --- URL state (#6) ---------------------------------------------------------
// The active tab and week offset live in the URL hash (#dashboard/w-2) so a
// refresh keeps your place, views are linkable, and the browser Back button
// navigates instead of exiting the app. Purely additive: state still lives in
// React; the hash is a mirror.
const TAB_IDS = ['final', 'dashboard', 'leave', 'staff', 'rankMaps', 'skills',
                 'shiftTypes', 'operations', 'settings'];
const parseHash = () => {
  const parts = window.location.hash.replace(/^#/, '').split('/');
  const tab = TAB_IDS.includes(parts[0]) ? parts[0] : 'final';
  const week = parts[1] && /^w-?\d+$/.test(parts[1]) ? parseInt(parts[1].slice(1), 10) : 0;
  return { tab, week };
};

const ScheduleDashboard = () => {
  // --- 1. CORE LOGIC (Custom Hooks) ---
  const { 
      schedule, setSchedule, loading, solving, error, notice, setNotice,
    loadDemoData: loadDemoDataRaw, fetchWeekRange, startSolving, stopSolving, publishSchedule
  } = useRoster();

  const { 
    selectionStart, setSelectionStart, selectionEnd, setSelectionEnd, 
    isDraggingSelection, setIsDraggingSelection, isCellSelected 
  } = useSelection();

  // --- 2. UI STATE ---
  const [activeTab, setActiveTab] = useState(() => parseHash().tab);
  const [gridMode, setGridMode] = useState('location');
  const [selectedView, setSelectedView] = useState('calendar');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedWeek, setSelectedWeek] = useState(() => parseHash().week);
  const [timeView, setTimeView] = useState('weekly');
  // Solve scope (Option 1): the console keeps SHOWING a week, but the solve can
  // cover a larger calendar window. Decoupled from the display grid; the Solve
  // button is labelled with the scope so it's clear more than the visible week
  // is solved. Values: 'weekly' | 'biweekly' | 'monthly'.
  const [solveScope, setSolveScope] = useState('weekly');
  // Dashboard weekend visibility. Display-only (weekend shifts are always fetched;
  // the toggle just shows/hides the Sat/Sun columns) — it does not affect the fetch
  // window or the solve. The default is DERIVED from whether the department actually
  // operates on weekends (see the effect below): a weekend-operating department
  // shows weekends by default; a weekday-only one hides the empty Sat/Sun columns.
  // Once the user toggles manually, their choice is respected (weekendPrefTouched).
  const [showWeekends, setShowWeekends] = useState(false);
  const [weekendPrefTouched, setWeekendPrefTouched] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [onCallSchedule, setOnCallSchedule] = useState(null);
  // On-call changes are drafts until Publish, like clinical ones (they used to
  // auto-publish on every change). Tracked so the close-tab warning covers them.
  const [onCallDirty, setOnCallDirty] = useState(false);
  // What the console uses to edit on-call: every edit marks the draft dirty.
  const setOnCallDraft = useCallback((updater) => {
    setOnCallSchedule(updater);
    setOnCallDirty(true);
  }, []);
  const [isSolvingOnCall, setIsSolvingOnCall] = useState(false);

  // --- Draft protection (#5) -----------------------------------------------
  // The solved-but-unpublished roster lives ONLY in this browser tab
  // (/shifts-range returns published assignments; drafts exist in memory until
  // Publish). Track when unpublished work exists, badge it in the console header, and
  // warn before the tab is closed or refreshed.
  //
  // Tracked per DATE, not as one flag: publishing November must not clear the
  // warning while October's solve is still unpublished (services/draftDates.js).
  const [draftDates, setDraftDates] = useState([]);
  const draftDirty = draftDates.length > 0;
  // Monotonic edit counter, bumped alongside every draft mutation, so a
  // conflict-check result can be pinned to the exact edit generation it saw
  // (any later edit makes the stored result stale).
  const [draftVersion, setDraftVersion] = useState(0);
  const markDraft = useCallback((dates) => {
    setDraftDates((cur) => addDraftDates(cur, dates));
    setDraftVersion((v) => v + 1);
  }, []);
  // The day a slot falls on, from the slot itself (its start), never read out
  // of its id (Oct 2026). A ref, so the edit handlers below need not re-create
  // themselves on every schedule change.
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  const dayOfSlot = useCallback((shiftId) => {
    const slot = (scheduleRef.current?.shifts || []).find(x => x.id === shiftId);
    return slot ? toISODate(new Date(slot.start)) : null;
  }, []);
  // The window a solve covers, recorded when it starts (handleStartSolving):
  // by the time it finishes the user may have navigated elsewhere.
  const solveWindowRef = useRef([]);
  const prevSolvingRef = useRef(false);
  useEffect(() => {
    // A solve just finished delivering results into local state.
    if (prevSolvingRef.current && !solving) markDraft(solveWindowRef.current);
    prevSolvingRef.current = solving;
  }, [solving, markDraft]);
  useEffect(() => {
    if (!draftDirty && !onCallDirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draftDirty, onCallDirty]);
  // The universal refresh replaces local state with server truth wholesale —
  // after it there is no draft to lose. Same prop name so every existing
  // consumer picks this wrapper up unchanged.
  const loadDemoData = useCallback(async (...args) => {
    await loadDemoDataRaw(...args);
    setDraftDates([]);
  }, [loadDemoDataRaw]);
  // Publishing is what makes a draft durable — but only for the dates it
  // published. `windowISO` is the published window's days (a day with no
  // shifts still has to clear); without it, fall back to the shifts sent, and
  // an unscoped publish (the whole schedule) clears everything.
  const handlePublish = useCallback(async (scoped, windowISO) => {
    const ok = await publishSchedule(scoped);
    if (ok) {
      if (windowISO && windowISO.length) {
        setDraftDates((cur) => removeDraftDates(cur, windowISO));
      } else if (scoped && scoped.shifts) {
        setDraftDates((cur) => removeDraftDates(cur, scoped.shifts.map((s) => (s.start || '').slice(0, 10))));
      } else {
        setDraftDates([]);
      }
    }
    return ok;
  }, [publishSchedule]);

  // --- Error surfacing (#3) ------------------------------------------------
  // useRoster records fetch/solve errors in state but nothing rendered them.
  useEffect(() => { if (error) toast.error(error); }, [error]);
  // Informational, not a failure -- e.g. "you stopped this solve early".
  useEffect(() => { if (notice) { toast.info(notice); setNotice(null); } }, [notice, setNotice]);

  // --- URL <-> state sync (#6) ---------------------------------------------
  useEffect(() => {
    const desired = '#' + activeTab + (selectedWeek ? `/w${selectedWeek}` : '');
    if (window.location.hash !== desired) window.history.pushState(null, '', desired);
  }, [activeTab, selectedWeek]);
  useEffect(() => {
    const onNav = () => {
      const { tab, week } = parseHash();
      setActiveTab((t) => (t === tab ? t : tab));
      setSelectedWeek((w) => (w === week ? w : week));
    };
    window.addEventListener('hashchange', onNav);
    window.addEventListener('popstate', onNav);
    return () => {
      window.removeEventListener('hashchange', onNav);
      window.removeEventListener('popstate', onNav);
    };
  }, []);

  const handleSolveOnCall = async (month, year) => {
    setIsSolvingOnCall(true);
    try {
      const response = await apiFetch(`/api/on-call/solve?month=${month}&year=${year}`, { method: 'POST' });
      const data = await response.json();
      if (data.shifts) {
        // Merged by (date, role): ids differ between solved, hand-assigned and
        // imported duties, and merging by id left two holders for one night.
        setOnCallSchedule(prev => (prev ? { ...data, shifts: mergeOnCall(prev.shifts, data.shifts) } : data));
        setSchedule(prev => ({ ...prev, onCallRoster: mergeOnCall(prev.onCallRoster, data.shifts) }));
        setOnCallDirty(true);
        toast.info("On-call roster solved — review it, then press Publish to save it.");
      } else toast.error("Could not find a valid on-call solution.");
    } catch (error) { toast.error("Error solving on-call roster."); }
    finally { setIsSolvingOnCall(false); }
  };
  // Whether the department's constraint config permits one person covering two
  // wards in the same hours (a priced last resort). Read from the read-only
  // constraints projection so the assignment drawer follows the same policy as
  // the solver.
  const [crossCoverEnabled, setCrossCoverEnabled] = useState(false);
  // Human-readable label per constraint id (e.g. alternating_weekend_off ->
  // "Alternating Weekend Off"), reusing this SAME fetch -- the audit modal
  // needs it to show real constraint names instead of raw ids.
  const [constraintLabels, setConstraintLabels] = useState({});
  useEffect(() => {
    apiFetch('/api/constraints/active')
      .then(r => r.json())
      .then(entries => {
        const list = Array.isArray(entries) ? entries : [];
        // `allows_cross_cover` is derived server-side from the rule's
        // max_concurrent. This used to test `template === 'cross_coverage'`,
        // which silently stopped matching when that rule merged into
        // `concurrency` (Sep 2026) -- the drawer would have lost its
        // cross-cover mode with no error anywhere. A capability flag cannot
        // break that way, and it is also more accurate: `concurrency` at
        // max_concurrent 1 is the old no_overlapping and permits nothing.
        setCrossCoverEnabled(list.some(e =>
          e && e.scope === 'clinical' && e.allows_cross_cover));
        setConstraintLabels(Object.fromEntries(
          list.filter(e => e && e.id && e.label).map(e => [e.id, e.label])
        ));
      })
      .catch(() => { setCrossCoverEnabled(false); setConstraintLabels({}); });
  }, []);

  // Bulk Upload State
  // Read into the bulk-upload payload below but never set: no UI exposes a
  // "wipe locations" toggle, so this is permanently false. Kept (rather than
  // inlined as `false`) so the option is one line from being wired up again.
  const [wipeLocations] = useState(false);
  const [wipeStaffList, setWipeStaffList] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  // Result of the last batch upload; opens the results modal when rows were
  // rejected. The API returns them, but nothing rendered them before, so a
  // planner saw 'Processed 27 updates' and never learned about the other 5.
  const [uploadResult, setUploadResult] = useState(null);
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);

  const fileInputRef = useRef(null);

  // --- 4. HELPERS ---
  const getWeekDates = useCallback((weekOffset, viewMode = 'weekly', includeWeekends = true) => {
    // Window presets, all snapped to natural calendar boundaries so the
    // window-relative constraints (e.g. shift_count_limit window=week/month)
    // align: a week is always Mon-Sun, a month always 1st-to-end. This avoids
    // the mid-week/mid-month boundary under-count where a limit can't see
    // shifts from the same calendar period that fell outside the solve window.
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start;
    let daysToGenerate;

    if (viewMode === 'monthly') {
      // Calendar month containing (today + offset months). Snap to the 1st;
      // length = that month's day count.
      const base = new Date(today.getFullYear(), today.getMonth() + weekOffset, 1);
      start = base;
      daysToGenerate = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    } else {
      // weekly (7) or biweekly (14): snap to this week's Monday, then step by
      // whole windows so navigation moves a full week/fortnight at a time.
      const span = viewMode === 'biweekly' ? 14 : 7;
      const anchor = new Date(today);
      const day = today.getDay();                       // 0=Sun..6=Sat
      const mondayDiff = today.getDate() - day + (day === 0 ? -6 : 1);
      anchor.setDate(mondayDiff);                        // this week's Monday
      start = new Date(anchor);
      start.setDate(anchor.getDate() + (weekOffset * span));  // step by window span
      daysToGenerate = span;
    }

    const dates = Array.from({ length: daysToGenerate }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    });

    return includeWeekends ? dates : dates.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
  }, []);

  const weekDates = React.useMemo(() => getWeekDates(selectedWeek, timeView, true), [getWeekDates, selectedWeek, timeView]);
  const dashboardWeekDates = React.useMemo(() => getWeekDates(selectedWeek, 'weekly', showWeekends), [getWeekDates, selectedWeek, showWeekends]);
  // The actual window sent to the solver. Anchored to the currently-visible
  // week so it always CONTAINS what the user sees. weekly = the visible week;
  // biweekly = the fortnight starting at the visible week's Monday; monthly =
  // the calendar month containing the visible week. Calendar-snapped so the
  // window-relative constraints align. Weekends included so the solver sees the
  // full operational window (per-location patterns still gate which days generate).
  const computeSolveWindow = useCallback((scope) => {
    const viewWeek = getWeekDates(selectedWeek, 'weekly', true);   // Mon..Sun of the visible week
    const anchor = viewWeek[0];
    if (!anchor) return dashboardWeekDates;
    if (scope === 'monthly') {
      // The month is chosen by the visible week's SUNDAY (its last day):
      //  - a week wholly inside one month -> that month, 1st to last day;
      //  - a straddling week (e.g. Mon 31 Aug – Sun 6 Sep) -> the LATER month
      //    (September), leaving the earlier month's tail days (Aug 31)
      //    untouched. The window is always exactly 1st..last of one month, so
      //    consecutive monthly solves never overlap or override each other.
      const pivot = new Date(anchor); pivot.setDate(anchor.getDate() + 6);
      const first = new Date(pivot.getFullYear(), pivot.getMonth(), 1);
      const len = new Date(pivot.getFullYear(), pivot.getMonth() + 1, 0).getDate();
      return Array.from({ length: len }, (_, i) => {
        const d = new Date(first); d.setDate(first.getDate() + i); d.setHours(0, 0, 0, 0); return d;
      });
    }
    const span = scope === 'biweekly' ? 14 : 7;
    return Array.from({ length: span }, (_, i) => {
      const d = new Date(anchor); d.setDate(anchor.getDate() + i); d.setHours(0, 0, 0, 0); return d;
    });
  }, [getWeekDates, selectedWeek, dashboardWeekDates]);
  const solveWindowDates = React.useMemo(
    () => computeSolveWindow(solveScope), [computeSolveWindow, solveScope]);
  // Stable identity for the current solve window — a check result stamped with
  // a different key belongs to a window the user has since navigated away from.
  const solveWindowKey = React.useMemo(() => {
    if (!solveWindowDates || solveWindowDates.length === 0) return '';
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return `${iso(solveWindowDates[0])}_${iso(solveWindowDates[solveWindowDates.length - 1])}`;
  }, [solveWindowDates]);
  // Remember which days a solve rewrites, so its draft is marked against them
  // when it finishes. The console always sends its window; the fallback covers
  // a caller that does not.
  const handleStartSolving = useCallback((input) => {
    solveWindowRef.current = (input && input.solveWindowStart)
      ? datesBetween(input.solveWindowStart, input.solveWindowEnd)
      : (solveWindowDates || []).map(toISODate);
    return startSolving(input);
  }, [startSolving, solveWindowDates]);

  // Does any location operate on a weekend (day 5=Sat or 6=Sun)? Used to conditionally
  // surface the weekend toggle — no point showing it for a weekday-only department.
  const hasWeekendOps = React.useMemo(() => {
    // A department operates on weekends if any loaded shift lands on Sat/Sun.
    // JS getDay(): 0=Sun, 6=Sat.
    const shifts = schedule?.shifts || [];
    if (shifts.some(s => {
      if (!s?.start) return false;
      const day = new Date(s.start).getDay();
      return day === 0 || day === 6;
    })) return true;
    // Else from the shift PATTERNS (which session runs on which weekday), for a
    // window that happens to hold no weekend shift -- closed, or every weekend
    // clinic inactive. It used to read the retired generation-1
    // location_schedules here. day field: 5=Sat, 6=Sun.
    const patterns = schedule?.locationShiftPatterns || schedule?.location_shift_patterns || {};
    return Object.values(patterns).some(items =>
      Array.isArray(items) && items.some(i => i && (i.day === 5 || i.day === 6))
    );
  }, [schedule]);

  // Default the weekend view to match whether the department operates on weekends,
  // until the user makes an explicit choice. Without this, a weekend-operating
  // department (e.g. ED's MAIN OPS running Sat/Sun) hid its weekend columns by
  // default, so the weekend "disappeared" and had to be toggled on every time.
  React.useEffect(() => {
    if (!weekendPrefTouched) {
      setShowWeekends(hasWeekendOps);
    }
  }, [hasWeekendOps, weekendPrefTouched]);

  // Wrap the toggle so a manual change pins the user's preference (stops the
  // derivation above from overriding it on the next schedule change). Accepts
  // either a value or a functional updater, matching setState's contract, since
  // the DashboardTab toggle calls it as setShowWeekends(v => !v).
  const handleSetShowWeekends = React.useCallback((next) => {
    setWeekendPrefTouched(true);
    setShowWeekends(prev => (typeof next === 'function' ? next(prev) : next));
  }, []);

  // --- 3. EFFECTS ---
  useEffect(() => {
    // Initial fetch of the roster state instead of forcing demo data
    const initApp = async () => {
      try {
        if (weekDates && weekDates.length > 0) {
          // Always fetch the FULL week (never weekdays-only) so weekend shifts are
          // present for hasWeekendOps to detect. The showWeekends toggle controls
          // DISPLAY only (which columns render), decoupled from what we fetch —
          // otherwise a weekday-only initial fetch hides weekend shifts and the
          // auto-detection can never turn the weekend view on.
          await fetchWeekRange(weekDates[0], weekDates[weekDates.length - 1], false);
        }
        // Load the FULL published clinical roster (all weeks, incl. weekends) so
        // the Final Roster shows every published week on launch -- not just the
        // console's visible window. Merge by shift id so the visible-week working
        // state from fetchWeekRange is preserved; published shifts fill the rest.
        try {
          const pubRes = await apiFetch('/api/published-roster');
          const pub = await pubRes.json();
          if (pub && Array.isArray(pub.shifts) && pub.shifts.length > 0) {
            setSchedule(prev => {
              if (!prev) return pub;
              const prevIds = new Set((prev.shifts || []).map(s => s.id));
              // Keep everything already in state (the freshly-fetched visible
              // week), add published shifts not already present.
              const merged = [
                ...(prev.shifts || []),
                ...pub.shifts.filter(s => !prevIds.has(s.id)),
              ];
              // Preserve prev's working state, but ensure the published metadata
              // the Final Roster's isPublished gate reads is present.
              return {
                ...pub,
                ...prev,
                shifts: merged,
                published_assignments: prev.published_assignments || pub.published_assignments,
                last_published_at: prev.last_published_at || pub.last_published_at,
              };
            });
          }
        } catch (e) { console.error("Published roster load failed", e); toast.error("Could not load the published roster."); }
      } catch (e) { console.error("Initialization failed", e); toast.error("Could not load data from the server — is the backend running?"); }
    };
    initApp();
  }, [fetchWeekRange, weekDates, setSchedule]);

  // When the solve scope extends beyond the visible week (biweekly/monthly),
  // fetch the full window so its shifts exist in state before solving --
  // otherwise a month-scope solve would only see the visible week's shifts.
  // fetchWeekRange merges into existing shifts, so this augments rather than
  // replaces the visible week.
  useEffect(() => {
    if (solveScope === 'weekly') return;   // visible week already fetched above
    if (solveWindowDates && solveWindowDates.length > 0) {
      // Full window (not weekdays-only) so weekend shifts exist for detection and
      // solving; display filtering is handled separately by showWeekends.
      fetchWeekRange(solveWindowDates[0], solveWindowDates[solveWindowDates.length - 1], false);
    }
  }, [fetchWeekRange, solveScope, solveWindowDates]);

  // Shifts indexed by local date, rebuilt only when the shifts or the filters
  // change. The console asks for one day's shifts once per grid cell, and the
  // tab holds the whole published roster (thousands of shifts): scanning all of
  // them per cell, on every render, was most of the console's lag. A stable
  // function identity also lets the console's own memos actually hold.
  const shiftsByDay = React.useMemo(() => {
    const index = new Map();
    for (const s of schedule?.shifts || []) {
      if (selectedLocation !== 'all' && s.location !== selectedLocation) continue;
      const ds = String(s.start || '').slice(0, 10);
      if (!index.has(ds)) index.set(ds, []);
      index.get(ds).push(s);
    }
    return index;
  }, [schedule?.shifts, selectedLocation]);

  const getShiftsForDay = React.useCallback((date) => {
    // Use local date parts to avoid UTC timezone shifts
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return shiftsByDay.get(`${y}-${m}-${d}`) || [];
  }, [shiftsByDay]);

  // Derived once per schedule, not on every App render.
  const locationCoverage = React.useMemo(() => getLocationCoverage(schedule), [schedule]);
  const employeeStats = React.useMemo(() => getEmployeeStats(schedule), [schedule]);

  const getEmployeeColor = (name) => {
    if (!name) return 'hsl(210, 20%, 80%)';
    if (schedule?.employees) {
      const sorted = [...schedule.employees].map(e => e.name).sort();
      const idx = sorted.indexOf(name);
      if (idx !== -1) return `hsl(${(idx * 137.5) % 360}, 70%, 55%)`;
    }
    return `hsl(0, 0%, 80%)`;
  };

  // --- Assignment validation (flow B: instant swap, alert only when the
  // resulting roster is INFEASIBLE, i.e. the change breaks a hard rule) ---
  const [assignmentAlert, setAssignmentAlert] = useState(null);   // {shiftId, prevEmployee, employeeName, ...report} | null
  const [, setValidating] = useState(false);
  // Per-shift call counter: guards against a slow, superseded validation
  // resolving AFTER a newer reassignment of the same shift and popping a
  // stale alert (or worse, a Revert that wipes the newer, legitimate pick).
  const validationTokenRef = useRef({});

  // Score the roster with a proposed swap already applied. On infeasibility we
  // surface an alert offering to revert; costly-but-legal changes pass silently
  // (the user can still inspect impact elsewhere).
  const validateAssignment = useCallback(async (shiftId, employee, prevEmployee, prevLocked) => {
    const employeeName = employee?.name || null;
    const myToken = (validationTokenRef.current[shiftId] || 0) + 1;
    validationTokenRef.current[shiftId] = myToken;
    try {
      setValidating(true);
      // Scope the validation to the current planning window. Without this the
      // backend cannot pin out-of-window shifts and scores the employee's ENTIRE
      // data range — slow, and it surfaces violations from outside the window the
      // planner is working in. Send the window as ISO dates alongside the schedule.
      const toISO = (d) => {
        const dt = new Date(d); dt.setHours(0, 0, 0, 0);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      };
      const win = (solveWindowDates && solveWindowDates.length > 0)
        ? { solve_window_start: toISO(solveWindowDates[0]),
            solve_window_end: toISO(solveWindowDates[solveWindowDates.length - 1]) }
        : {};
      const res = await apiFetch('/api/validate-assignment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule: { ...schedule, ...win, shifts: scopeShifts(schedule.shifts, win.solve_window_start, win.solve_window_end) }, shift_id: shiftId, staff_id: employee?.id || null }),
      });
      const data = await res.json();
      // A newer assignment of this same shift has already been issued --
      // this response is stale and must not surface an alert about a pick
      // that's no longer current.
      if (validationTokenRef.current[shiftId] !== myToken) return;
      if (data && data.success && data.feasible === false) {
        setAssignmentAlert({
          shiftId,
          prevEmployee: prevEmployee || null,
          prevLocked: !!prevLocked,
          employeeName: employeeName || null,
          reason: data.infeasible_reason,
        });
      }
    } catch (e) {
      console.error('Assignment validation failed', e);
      toast.error('Could not validate this assignment against the rules.');
    } finally {
      setValidating(false);
    }
  }, [schedule, solveWindowDates]);

  // On-demand, roster-wide validation against the REAL constraint framework
  // (#3.10). This is the authoritative counterpart to the always-on client-side
  // fulfilment/leave heuristics in DashboardTab: those run live and cheap; this
  // runs the full constraint scan only when the user clicks "Check conflicts".
  // Result shape: { feasible, infeasible_reason, violations:[{id,description,
  // weighted_penalty,tier}] }. Shares its backend core (score_roster) with the
  // swap validator and the future pinned-solve.
  const [rosterCheck, setRosterCheck] = useState(null);       // last result
  const [rosterChecking, setRosterChecking] = useState(false); // in-flight
  const checkRosterConflicts = useCallback(async () => {
    if (!schedule) return null;
    setRosterChecking(true);
    try {
      const toISO = (d) => {
        const dt = new Date(d); dt.setHours(0, 0, 0, 0);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      };
      const win = (solveWindowDates && solveWindowDates.length > 0)
        ? { solve_window_start: toISO(solveWindowDates[0]),
            solve_window_end: toISO(solveWindowDates[solveWindowDates.length - 1]) }
        : {};
      const res = await apiFetch('/api/validate-roster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule: { ...schedule, ...win, shifts: scopeShifts(schedule.shifts, win.solve_window_start, win.solve_window_end) } }),
      });
      const data = await res.json();
      // Stamp the result with the edit generation and window it saw, so it can
      // be recognised as stale the moment either changes (see currentRosterCheck).
      const result = { ...data, checkedAt: new Date().toISOString(),
                       draftVersion, windowKey: solveWindowKey };
      setRosterCheck(result);
      return result;
    } catch (e) {
      console.error('Roster validation failed', e);
      const result = { success: false, error: true, checkedAt: new Date().toISOString(),
                       draftVersion, windowKey: solveWindowKey };
      setRosterCheck(result);
      return result;
    } finally {
      setRosterChecking(false);
    }
  }, [schedule, solveWindowDates, draftVersion, solveWindowKey]);

  // A check result is CURRENT only while nothing has changed since it ran:
  // same edit generation AND same solve window. Downstream needs zero
  // staleness logic — a stale result simply arrives as null.
  const currentRosterCheck = (rosterCheck
    && rosterCheck.draftVersion === draftVersion
    && rosterCheck.windowKey === solveWindowKey) ? rosterCheck : null;

  // --- 5. HANDLERS ---
  // Manually picking someone auto-locks the slot: the next Solve treats it as
  // a fixed fact, not a decision to optimise over. `opts.locked` lets a caller
  // (the revert flow below) override that default to restore an exact prior
  // state instead.
  const handleClinicalAssign = useCallback((shiftId, employee, opts = {}) => {
    let prevEmployee = null;
    let prevLocked = false;
    setSchedule(prev => {
      if (!prev || !prev.shifts) return prev;
      const newShifts = prev.shifts.map(s => {
        if (s.id === shiftId) {
          prevEmployee = s.employee || null;
          prevLocked = !!s.locked;
          const locked = opts.locked !== undefined ? opts.locked : !!employee;
          return { ...s, employee: employee, locked };
        }
        return s;
      });
      return { ...prev, shifts: newShifts };
    });
    markDraft([dayOfSlot(shiftId)]);
    // Flow B: validate a real placement (skip reverts, which pass {silent:true}).
    if (employee && !opts.silent) {
      validateAssignment(shiftId, employee, prevEmployee, prevLocked);
    }
  }, [setSchedule, validateAssignment, markDraft, dayOfSlot]);

  const handleClinicalUnassign = useCallback((shiftId) => {
    setSchedule(prev => {
      if (!prev || !prev.shifts) return prev;
      const newShifts = prev.shifts.map(s => {
        if (s.id === shiftId) {
          return { ...s, employee: null, locked: false };
        }
        return s;
      });
      return { ...prev, shifts: newShifts };
    });
    markDraft([dayOfSlot(shiftId)]);
  }, [setSchedule, markDraft, dayOfSlot]);

  // Declared AFTER handleClinicalAssign because it depends on it: listing that
  // dependency honestly (rather than an empty array, which captured the very
  // first render's copy forever) means the reference has to already exist.
  const revertAssignment = useCallback(() => {
    setAssignmentAlert(alert => {
      if (alert) handleClinicalAssign(alert.shiftId, alert.prevEmployee, { silent: true, locked: alert.prevLocked });
      return null;
    });
  }, [handleClinicalAssign]);

  // Flip a slot's lock without touching who holds it — the "let the solver
  // reconsider this one" action for an already-solved or already-locked cell.
  const handleClinicalToggleLock = useCallback((shiftId) => {
    setSchedule(prev => {
      if (!prev || !prev.shifts) return prev;
      const newShifts = prev.shifts.map(s => (
        s.id === shiftId ? { ...s, locked: !s.locked } : s
      ));
      return { ...prev, shifts: newShifts };
    });
    markDraft([dayOfSlot(shiftId)]);
  }, [setSchedule, markDraft, dayOfSlot]);

  // Wipe every assignment and lock inside a caller-supplied window of ISO
  // dates (the console computes the window — solve scope or visible week,
  // same rule as Publish — since it already owns that date logic). Purely
  // local: nothing is persisted until Publish, so this never touches the
  // backend, matching every other draft mutation on this page.
  const handleClearWeek = useCallback((windowISO) => {
    setSchedule(prev => {
      if (!prev || !prev.shifts) return prev;
      const newShifts = prev.shifts.map(s => (
        windowISO.includes((s.start || '').slice(0, 10))
          ? { ...s, employee: null, locked: false }
          : s
      ));
      return { ...prev, shifts: newShifts };
    });
    markDraft(windowISO);
  }, [setSchedule, markDraft]);

  // Free-text label for a specific assignment. Display-only; rides along in the
  // normal schedule state and is persisted on publish. Empty string clears it.
  const handleClinicalLabel = useCallback((shiftId, label) => {
    setSchedule(prev => {
      if (!prev || !prev.shifts) return prev;
      const newShifts = prev.shifts.map(s => {
        if (s.id === shiftId) {
          return { ...s, assignmentLabel: label || null };
        }
        return s;
      });
      return { ...prev, shifts: newShifts };
    });
    markDraft([dayOfSlot(shiftId)]);
  }, [setSchedule, markDraft, dayOfSlot]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]; if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('skip_existing', "false");
    fd.append('wipe_staff', wipeStaffList.toString()); fd.append('wipe_locations', wipeLocations.toString());
    try {
      const res = await apiFetch('/api/batch-upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        const notes = Array.isArray(data.errors) ? data.errors.length : 0;
        const rejected = data.rejected ?? notes;
        const applied = data.applied ?? 0;
        // Partial success: the applied rows are already committed server-side,
        // so never frame this as a failure. Clean uploads stay a single toast.
        if (rejected > 0) {
          toast.error(`${applied} applied, ${rejected} ${rejected === 1 ? 'row' : 'rows'} not applied`);
          setUploadResult(data);
        } else if (notes > 0) {
          // Warnings only: everything applied, so keep the success framing and
          // let the modal explain what was ignored.
          toast.success(data.message);
          setUploadResult(data);
        } else {
          toast.success(data.message);
        }
        loadDemoData('SMALL');
      }
      else toast.error(`Error: ${data.message}`);
    } catch (err) { toast.error("Upload failed."); }
    finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const downloadTemplate = async (type) => {
    try {
      const res = await apiFetch(`/api/batch-template?type=${type}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `IntelliRoster_${type.charAt(0).toUpperCase() + type.slice(1)}_Template.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) { console.error(e); }
  };

  const publishOnCall = useCallback(async (scheduleToPublish, silent = false) => {
    if (!scheduleToPublish) return;
    try {
      const res = await apiFetch('/api/on-call/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleToPublish)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json().catch(() => ({}));
      // A duty held by somebody no longer on the staff list was not saved; drop
      // that holder here too, so the screen matches what the server has.
      const skippedIds = new Set((body?.skipped || []).map(s => s.shift_id));
      const saved = (scheduleToPublish.shifts || []).map(s => (skippedIds.has(s.id) ? { ...s, employee: null } : s));

      // Keep the clinical schedule's copy in step (post-call rules read it).
      // No full reload: that used to discard any unpublished CLINICAL roster.
      setSchedule(prev => (prev ? { ...prev, onCallRoster: mergeOnCall(prev.onCallRoster, saved) } : prev));
      setOnCallDirty(false);
      const skipped = skippedMessage(body?.skipped);
      if (skipped) toast.error(skipped);             // even when silent: something did not save
      else if (!silent) toast.success("On-call roster published.");
    } catch (err) { 
      console.error(err);
      if (!silent) toast.error("Failed to publish the on-call roster. Nothing was saved — try again.");
    }
  }, [setSchedule]);

  // The published on-call roster, loaded ONCE. It used to be reloaded on every
  // week change and re-published a second later (auto-save), which wrote to the
  // database on every navigation and made every on-call solve a publish.
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/on-call/load')
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setOnCallSchedule(data && data.shifts && data.shifts.length > 0 ? data : null);
        setOnCallDirty(false);
      })
      .catch(e => console.error('On-call load failed', e));
    return () => { cancelled = true; };
  }, []);

  const handleAddEmployee = async (emp) => {
    try {
      const res = await apiFetch('/api/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emp)
      });
      const data = await res.json();
      if (data.success) loadDemoData('SMALL');
      else toast.error("Failed to add the employee.");
    } catch (err) { console.error(err); toast.error("Request failed — is the backend running?"); }
  };

  const handleUpdateEmployee = async (emp) => {
    try {
      // By permanent id: the name in the body may be a rename.
      const res = await apiFetch(`/api/employees/${encodeURIComponent(emp.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emp)
      });
      const data = await res.json();
      if (data.success) {
        // Update local state instead of reloading everything
        setSchedule(prev => {
          if (!prev || !prev.employees) return prev;
          const { team, ...fields } = emp;
          const updatedEmps = prev.employees.map(e => {
            if (e.id === emp.id) {
              return { ...e, ...fields };
            }
            return e;
          });
          // A team is not a field on the person (services/teams.js).
          const teams = team === undefined ? prev.teams : withTeam(prev.teams, emp.id, team);
          return { ...prev, employees: updatedEmps, teams };
        });
      } else {
        toast.error("Failed to update the employee.");
      }
    } catch (err) { console.error(err); toast.error("Request failed — is the backend running?"); }
  };

  // Close (or reopen) every clinic and OT on one date -- a public holiday. The
  // server sets the state (not a blind toggle); the visible week is then
  // re-fetched, which drops that day's slots or brings them back with their
  // published holders, leaving drafts on other dates untouched.
  const handleSetClosedDate = async (dateIso, closed) => {
    try {
      const res = await apiFetch(`/api/closed-dates/${dateIso}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ closed }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (weekDates && weekDates.length > 0) {
        await fetchWeekRange(weekDates[0], weekDates[weekDates.length - 1], false);
      }
      toast.info(closed ? `Clinics and OTs closed on ${dateIso}.` : `Clinics and OTs reopened on ${dateIso}.`);
    } catch (err) {
      console.error(err);
      toast.error('Could not change the closed date.');
    }
  };

  const handleRemoveEmployee = async (staffId) => {
    try {
      const res = await apiFetch(`/api/employees/${encodeURIComponent(staffId)}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) loadDemoData('SMALL');
      else toast.error("Failed to remove the employee.");
    } catch (err) { console.error(err); toast.error("Request failed — is the backend running?"); }
  };

  // Read the filename the server chose. It is named after the exported window,
  // and the endpoint already sets Access-Control-Expose-Headers so this is
  // readable cross-origin too.
  const filenameFromResponse = (response) => {
    const cd = response.headers.get('Content-Disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return m ? decodeURIComponent(m[1]) : null;
  };

  const exportToExcel = async (windowDays = 7, targetDate = null) => {
    try {
      const response = await apiFetch('/api/reports/excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Pass BOTH the window length (7 or 14) and the first day of the window
        // on screen, so the export matches what the planner is looking at.
        // Sending only the length let the server fall back to its own
        // `date.today()` -- always the current week, and on a UTC container not
        // even the planner's today.
        body: JSON.stringify({
          ...schedule,
          // Only the exported days (a week either side for safety): the sheet
          // reads nothing else, and the full roster was ~2 MB per download.
          shifts: targetDate ? exportShifts(schedule.shifts, targetDate, windowDays) : schedule.shifts,
          export_window_days: windowDays,
          export_target_date: targetDate || null,
        })
      });
      if (!response.ok) { toast.error('Excel export failed.'); return; }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Prefer the server's filename: it is derived from the exported window, so
      // two different weeks cannot land as the same name (which reads as "it
      // keeps giving me the same week"). Fall back to the window we asked for.
      a.download = filenameFromResponse(response)
        || `IntelliRoster_Roster_${targetDate || new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Excel export failed", err); toast.error('Excel export failed.'); }
  };

  const exportToPDF = () => {
    window.print();
  };

  return (
    <div className="h-screen min-h-screen flex bg-slate-50 overflow-hidden font-body">
      <div className="no-print h-full">
        {assignmentAlert && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in" onClick={() => setAssignmentAlert(null)}>
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md m-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 px-5 py-4 bg-rose-50 border-b border-rose-100">
                <div className="p-2 bg-white rounded-xl border border-rose-200 text-rose-500 shadow-sm">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-rose-900 tracking-tight">Assignment breaks a hard rule</h3>
                  <p className="text-[11px] text-rose-600 font-bold">This roster is now infeasible.</p>
                </div>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {assignmentAlert.reason || 'The assignment violates a hard constraint.'}
                </p>
                {assignmentAlert.employeeName && (
                  <div className="text-[11px] font-bold text-slate-400">
                    {assignmentAlert.employeeName} was placed{assignmentAlert.prevEmployee ? `, replacing ${assignmentAlert.prevEmployee.name}` : ''}.
                  </div>
                )}
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  You can keep the change (a manual override) and it will remain in the roster, or revert to the previous assignment.
                </p>
              </div>
              <div className="flex gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
                <button onClick={revertAssignment} className="flex-1 px-4 py-2 rounded-xl bg-white border-2 border-slate-200 text-xs font-bold text-slate-600 hover:border-slate-300 transition-all">
                  Revert
                </button>
                <button onClick={() => setAssignmentAlert(null)} className="flex-1 px-4 py-2 rounded-xl bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 transition-all shadow-sm">
                  Keep anyway
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastHost />
        <UploadResultsModal
          isOpen={!!uploadResult}
          onClose={() => setUploadResult(null)}
          result={uploadResult}
          shiftCodes={((schedule?.shiftTypes || schedule?.shift_types) || [])
            .filter(t => t && (t.isActive ?? t.is_active ?? true))
            .map(t => String(t.shortCode || t.short_code || t.id || '').trim())
            .filter(Boolean)}
        />
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1600px] mx-auto p-8 space-y-8">
            {activeTab === 'final' && (
              <ErrorBoundary label="The Final Roster">
              <FinalRosterTab 
                schedule={schedule} 
                exportToExcel={exportToExcel} 
                exportToPDF={exportToPDF}
                onViewConsole={() => setActiveTab('dashboard')}
              />
              </ErrorBoundary>
            )}
            
            {activeTab === 'dashboard' && (
              <ErrorBoundary label="The management console">
              <DashboardTab 
                crossCoverEnabled={crossCoverEnabled}
                constraintLabels={constraintLabels}
                draftDirty={draftDirty}
                draftDates={draftDates}
                schedule={schedule} loading={loading} solving={solving} loadDemoData={loadDemoData}
                showLoadDropdown={showLoadDropdown} setShowLoadDropdown={setShowLoadDropdown}
                selectedView={selectedView} setSelectedView={setSelectedView}
                startSolving={handleStartSolving}
                stopSolving={stopSolving}
                selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}
                selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek}
                gridMode={gridMode} setGridMode={setGridMode} weekDates={dashboardWeekDates}
                solveWindowDates={solveWindowDates} solveScope={solveScope} setSolveScope={setSolveScope}
                solverStatus={schedule?.solverStatus || schedule?.solver_status}
                computeSolveWindow={computeSolveWindow}
                showWeekends={showWeekends} setShowWeekends={handleSetShowWeekends} hasWeekendOps={hasWeekendOps}
                getAvailableEmployeesForDay={getAvailableEmployeesForDay}
                locationCoverage={locationCoverage} employeeStats={employeeStats}
                getDailyStaffingBuffer={() => []} exportToExcel={() => {}} exportToPDF={() => {}} 
                getShiftsForDay={getShiftsForDay} getEmployeeColor={getEmployeeColor}
                isEmployeeUnavailable={isEmployeeUnavailable}
                publishSchedule={handlePublish}
                fetchWeekRange={fetchWeekRange}
                onSetClosedDate={handleSetClosedDate}
                // ON-CALL
                onCallSchedule={onCallSchedule}
                setOnCallSchedule={setOnCallDraft}
                onCallDirty={onCallDirty}
                isSolvingOnCall={isSolvingOnCall}
                startSolvingOnCall={handleSolveOnCall}
                publishOnCall={publishOnCall}
                onClinicalAssign={handleClinicalAssign}
                onClinicalUnassign={handleClinicalUnassign}
                onClinicalToggleLock={handleClinicalToggleLock}
                onClearWeek={handleClearWeek}
                onClinicalLabel={handleClinicalLabel}
                onCheckConflicts={checkRosterConflicts}
                rosterCheck={currentRosterCheck}
                rosterChecking={rosterChecking}
              />
              </ErrorBoundary>
            )}

            {activeTab === 'leave' && (
              <ErrorBoundary label="The leave roster">
              <LeaveRosterTab 
                schedule={schedule} loading={loading} solving={solving}
                selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek}
                timeView={timeView} setTimeView={setTimeView}
                displayDates={weekDates} isEmployeeUnavailable={isEmployeeUnavailable}
                getEmployeeColor={getEmployeeColor} isCellSelected={isCellSelected}
                setSelectionStart={setSelectionStart} setSelectionEnd={setSelectionEnd}
                setIsDraggingSelection={setIsDraggingSelection} isDraggingSelection={isDraggingSelection}
                selectionStart={selectionStart} selectionEnd={selectionEnd}
                downloadTemplate={downloadTemplate} handleFileUpload={handleFileUpload} fileInputRef={fileInputRef}
                loadDemoData={loadDemoData}
              />
              </ErrorBoundary>
            )}

            {activeTab === 'staff' && (
              <ErrorBoundary label="The staff list">
              <StaffTab 
                schedule={schedule} filterText={filterText} setFilterText={setFilterText}
                loading={loading} solving={solving} handleFileUpload={handleFileUpload}
                downloadTemplate={downloadTemplate} getEmployeeColor={getEmployeeColor}
                fileInputRef={fileInputRef} showUploadOptions={showUploadOptions} 
                setShowUploadOptions={setShowUploadOptions} wipeStaffList={wipeStaffList} 
                setWipeStaffList={setWipeStaffList} loadDemoData={loadDemoData}
                onAddEmployee={handleAddEmployee}
                onUpdateEmployee={handleUpdateEmployee}
                onRemoveEmployee={handleRemoveEmployee}
                allLocations={schedule?.locations || []}
                allSkills={getAllSkills(schedule)}
              />
              </ErrorBoundary>
            )}

            {activeTab === 'rankMaps' && (
              <ErrorBoundary label="Ranks & positions">
              <RankMapPage onRolesSaved={() => loadDemoData('SMALL')} />
              </ErrorBoundary>
            )}

            {activeTab === 'skills' && (
              <ErrorBoundary label="Skills">
              <SkillsTab schedule={schedule} loadDemoData={loadDemoData} getEmployeeColor={getEmployeeColor} />
              </ErrorBoundary>
            )}

            {activeTab === 'shiftTypes' && (
              <ErrorBoundary label="Shift types">
              <ShiftTypesPage />
              </ErrorBoundary>
            )}

            {activeTab === 'operations' && (
              <ErrorBoundary label="Clinical operations">
              <OperationsTab 
                schedule={schedule} loading={loading} solving={solving}
                handleFileUpload={handleFileUpload} downloadTemplate={downloadTemplate}
                fileInputRef={fileInputRef} getEmployeeColor={getEmployeeColor}
                loadDemoData={loadDemoData}
              />
              </ErrorBoundary>
            )}

            {activeTab === 'settings' && (
              <ErrorBoundary label="Settings">
              <SettingsTab 
                schedule={schedule} loadDemoData={loadDemoData}
                onOnCallCleared={() => { setOnCallSchedule(null); setOnCallDirty(false); }}
                onRosterFreeRowsChange={(on) => setSchedule(prev => (prev ? { ...prev, rosterFreeRows: on } : prev))}
              />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ScheduleDashboard;
